import { CircularProgress, Grid, Typography } from '@material-ui/core';
import { Button, Dialog, Icons, Select } from '@nutrien/cxp-components';
import * as Sentry from '@sentry/react';
import { observer } from 'mobx-react-lite';
import postal from 'postal';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { deficiencyFiles, inspectionFiles, userRole } from '../../graphql';
import { useMst } from '../../mobx-models/Root';
import { IDeficiencyFilesFeed, IInspectionFilesFeed } from '../../models/models';
import { isJestTest } from '../../test-helpers/isJestTest';
import {
  mockSiteDisciplineData,
  mockUserroleData,
} from '../../test-helpers/mockData/siteDisciplinePopUp';
import { sentryCaptureException } from '../../utilities/sentryCaptureException';
import { useNotification } from '../../utilities/useNotification';
import { FALSE, IMAGE_PATH, TRUE, WORK_PACKAGE_NAV_TAB } from '../../utilities/constants';
import { ApiStatus } from '../../utilities/enums';
import { TrackingEventType, useEventTracking } from '../../utilities/hooks/useEventTracking';
import {
  getValueFromLocalStorage,
  isNull,
  setValueinLocalStorage,
} from '../../utilities/utilityFunctions';
import { logError } from '../Shared/analytics';
import useStyles from './SiteDisciplinePopUp.styles';

interface IDisciplines {
  disciplineId: number;
  disciplineName: string;
  totalSchedules: number;
}

interface ISiteDisciplinePopUp {
  siteId: number;
  siteName: string;
  inspectorId: number;
  clerkId: number;
  dateModified: string;
  disciplines: IDisciplines[];
}
interface Props {
  open: boolean;
  onClose: () => void;
}

const SiteDisciplinePopUp: React.FC<Props> = ({ open, onClose }: Props) => {
  const classes = useStyles();
  const history = useHistory();
  const { location } = window;
  const { SiteDiscipline, primaryContext, syncData, WorkPackageTabs, CurrentUserSelected } =
    useMst();
  const { errorNotification } = useNotification();
  const { trackEvent } = useEventTracking();

  const [selectedSite, setSelectedSite] = useState<string>('');
  const [selectedDiscipline, setSelectedDiscipline] = useState<string>('');
  const [siteDisciplineObject, setSiteDisciplineObject] = useState<{ [key: string]: string[] }>();
  const [onConfirmSiteDisciplineClicked, setOnConfirmSiteDiscipline] = useState<boolean>(false);

  const siteDisciplineDocument = isJestTest()
    ? mockSiteDisciplineData
    : primaryContext.primaryContextList;
  const NO_WP_ASSIGNED =
    primaryContext.PrimaryContextListFetchStatus === ApiStatus.SUCCESS &&
    siteDisciplineDocument.length === 0;
  const LOADING = primaryContext.PrimaryContextListFetchStatus === ApiStatus.LOADING;
  const ERROR = primaryContext.PrimaryContextListFetchStatus === ApiStatus.ERROR;

  const parseLocalStorage = (data: string) => {
    if (data) return JSON.parse(getValueFromLocalStorage(data) || '{}');
  };

  const setup = async () => {
    try {
      await primaryContext.getSiteDisciplineList();
    } catch (error) {
      logError(error, false, 'file: SiteDisciplinePopUp.tsx ~ setup ~ error');
    }
  };

  useEffect(() => {
    setup();
  }, []);

  const setUserRole = async () => {
    if (siteDisciplineDocument.length > 0) {
      setValueinLocalStorage('inspectorId', siteDisciplineDocument[0].inspectorId?.toString());
      setValueinLocalStorage('clerkId', siteDisciplineDocument[0].clerkId?.toString());
      const role = isJestTest() ? mockUserroleData : await userRole();
      const currentUser = {
        inspectorId: siteDisciplineDocument[0].inspectorId,
        clerkId: siteDisciplineDocument[0].clerkId,
        name: role.data.currentUserDetails.name,
        userId: role.data.currentUserDetails.id,
      };
      setValueinLocalStorage('currentUser', JSON.stringify(currentUser));
      CurrentUserSelected.setCurrentUser(currentUser);

      setValueinLocalStorage(
        'isLeadInspector',
        role.data.currentUserDetails.roles.includes(6) ? TRUE : FALSE,
      );
    }
  };

  const setSiteDiscipline = () => {
    const siteDisciplineRxObject: { [key: string]: string[] } = {};
    if (siteDisciplineDocument.length > 0) {
      siteDisciplineDocument.map((item: ISiteDisciplinePopUp) => {
        const temp: string[] = [];
        item.disciplines.forEach((disciplineObj: IDisciplines) => {
          if (disciplineObj.totalSchedules > 0) {
            temp.push(`${disciplineObj.disciplineName} (${disciplineObj.totalSchedules})`);
          }
        });
        if (temp.length) {
          siteDisciplineRxObject[item.siteName] = temp;
        }
      });
      setSiteDisciplineObject(siteDisciplineRxObject);
    }
  };

  useEffect(() => {
    setUserRole();
    setSiteDiscipline();
  }, [siteDisciplineDocument]);

  useEffect(() => {
    if (!selectedSite && siteDisciplineObject) {
      const defaultSiteSelected: string = parseLocalStorage('selectedSite')?.selectedSite;
      const hasSite = Object.keys(siteDisciplineObject).includes(defaultSiteSelected);
      if (hasSite) {
        setSelectedSite(defaultSiteSelected);
        const defaultDisciplineSelected =
          parseLocalStorage('selectedDiscipline')?.selectedDiscipline;
        const hasDiscipline =
          siteDisciplineObject[defaultSiteSelected]?.includes(defaultDisciplineSelected);
        if (hasDiscipline) {
          setSelectedDiscipline(defaultDisciplineSelected);
        } else {
          if (siteDisciplineObject && siteDisciplineObject[defaultSiteSelected]) {
            setSelectedDiscipline(siteDisciplineObject[defaultSiteSelected][0]);
          }
        }
      } else {
        const tempSite = Object.keys(siteDisciplineObject)[0];
        setSelectedSite(tempSite);
        if (siteDisciplineObject && siteDisciplineObject[tempSite]) {
          setSelectedDiscipline(siteDisciplineObject[tempSite][0]);
        }
      }
    }
  }, [siteDisciplineObject]);

  const handleChange = (value: string, type: string) => {
    if (type === 'site') {
      setSelectedSite(value);
      if (siteDisciplineObject && !siteDisciplineObject[value]?.includes(selectedDiscipline)) {
        setSelectedDiscipline('');
      }
    } else setSelectedDiscipline(value);
  };

  const disciplineMapping = (disciplineName: string) => {
    switch (disciplineName) {
      case 'Electrical':
        return 1;
      case 'Mechanical':
        return 2;
      case 'Structural':
        return 3;
      case 'Shaft':
        return 4;
      default:
        return 0;
    }
  };

  const getId = (siteName: string, disciplineName: string) => {
    let discipline = '';
    const selectedSiteId =
      siteDisciplineDocument.filter(item => item.siteName === siteName)[0]?.siteId || null;
    discipline = disciplineName.split('(')[0].trim();
    const selectedDisciplineId = disciplineMapping(discipline);
    return { selectedSiteId, selectedDisciplineId };
  };

  const onConfirmSiteDiscipline = async () => {
    try {
      const { selectedSiteId, selectedDisciplineId } = getId(selectedSite, selectedDiscipline);
      const oldSiteId = Number(
        JSON.parse(getValueFromLocalStorage('selectedSite') || '{}')?.selectedSiteId,
      );
      const oldDisciplineId = Number(
        JSON.parse(getValueFromLocalStorage('selectedDiscipline') || '{}')?.selectedDisciplineId,
      );
      if (oldSiteId === selectedSiteId && oldDisciplineId === selectedDisciplineId) {
        onClose();
      } else {
        if (selectedSite && selectedDiscipline && selectedSiteId && selectedDisciplineId) {
          syncData.setWorpackageSync(false);
          syncData.setInspectionFileSync(false);
          syncData.setDeficiencyFileSync(false);
          syncData.setPreviousReportSync(false);
          syncData.resetTotalCount();
          setValueinLocalStorage('assetFilesCount', 0?.toString());
          const site = { selectedSiteId, selectedSite };
          const discipline = { selectedDisciplineId, selectedDiscipline };
          localStorage.setItem('selectedSite', JSON.stringify(site));
          localStorage.setItem('selectedDiscipline', JSON.stringify(discipline));
          let wpCount = discipline?.selectedDiscipline?.substring(
            discipline?.selectedDiscipline?.indexOf('(') + 1,
          );
          wpCount = wpCount.substring(0, wpCount.length - 1);
          localStorage.setItem('wpCount', wpCount);
          SiteDiscipline.setWpCount(Number(wpCount));
          if (
            oldSiteId &&
            oldDisciplineId &&
            (oldSiteId !== selectedSiteId || oldDisciplineId !== selectedDisciplineId)
          ) {
            localStorage.setItem('oldSiteId', JSON.stringify(oldSiteId));
            localStorage.setItem('oldDisciplineId', JSON.stringify(oldDisciplineId));
            localStorage.setItem('lastRequested', '1900-01-01T01:01:01.1234567-06:00');
            SiteDiscipline.setRefreshWorkPackage(true);
            WorkPackageTabs.setTab(WORK_PACKAGE_NAV_TAB.WORKPACKAGE.VALUE);
          }
          setOnConfirmSiteDiscipline(true);
          let totalFilesToDownload = 0;
          await Promise.all([
            inspectionFiles(selectedSiteId, selectedDisciplineId),
            deficiencyFiles(selectedSiteId, selectedDisciplineId),
          ])
            .then(result => {
              localStorage.setItem('initialSync', 'true');
              const inspectionFilesCount = [
                ...new Map(
                  result[0].data.inspectionFilesFeed
                    ?.filter(
                      (e: IInspectionFilesFeed) =>
                        e.isDelete === FALSE &&
                        e?.siteId === selectedSiteId &&
                        e?.disciplineId === selectedDisciplineId &&
                        !e?.extension.includes('video') &&
                        ((e?.sizeMb < 100000000 && !e?.extension.includes('image')) ||
                          e?.extension.includes('image')),
                    )

                    .map((item: IInspectionFilesFeed) => [item.fileUUID, item]),
                ).values(),
              ].length;
              const deficiencyFilesCount = [
                ...new Map(
                  result[1].data.deficiencyFilesFeed
                    ?.filter(
                      (e: IDeficiencyFilesFeed) =>
                        e.isDelete === FALSE &&
                        e?.siteId === selectedSiteId &&
                        e?.disciplineId === selectedDisciplineId &&
                        !e?.isActiveDeficiencyFile,
                    )
                    .map((item: IDeficiencyFilesFeed) => [item.fileAimsUUID, item]),
                ).values(),
              ].length;
              const filesCount = {
                inspectionFilesCount,
                deficiencyFilesCount,
              };
              setValueinLocalStorage('FilesCount', JSON.stringify(filesCount));
              totalFilesToDownload = inspectionFilesCount + deficiencyFilesCount;
            })
            .finally(() => {
              setValueinLocalStorage('totalFilesToDownload', totalFilesToDownload);
              setValueinLocalStorage('totalFilesDownloaded', 0);
              syncData.setTotalFixCount(totalFilesToDownload);
              setOnConfirmSiteDiscipline(false);
              history.push('/work-packages');
            });
        }
        Sentry.setContext('siteMetadata', {
          siteName: selectedSite,
          disciplineName: selectedDiscipline.split('(')[0].trim(),
        });

        SiteDiscipline.setSiteDisciplineUpdateInitialSync(true);
        SiteDiscipline.setSiteDisciplineData();

        postal.publish({
          channel: 'site.discipline',
          topic: 'site.discipline.updated',
        });

        setOnConfirmSiteDiscipline(false);
        primaryContext.resetStore();
        onClose();
      }
    } catch (error) {
      sentryCaptureException(
        error,
        '~ file: SiteDisciplinePopUp.tsx ~ line 240 ~ onConfirmSiteDiscipline ~',
      );
      await trackEvent(TrackingEventType.SERVICE_FAILED, {
        initialLoad: true,
        error: JSON.stringify(error),
      });
    }
  };

  const handleKeyEvent = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      onConfirmSiteDiscipline();
    }
  };

  if (ERROR) {
    errorNotification('Response not successful: Received status code 500');
    onClose();
  }
  return (
    <>
      <Dialog
        open={open}
        onClose={() => {
          if (location.pathname.indexOf('home') === -1) {
            primaryContext.resetStore();
            onClose();
          }
        }}
        titleText={!LOADING ? 'Site Selection' : ''}
        backButton={false}
        onKeyPress={handleKeyEvent}
        data-testid="dialogId"
        dialogActions={
          <>
            {NO_WP_ASSIGNED ? (
              <Grid container justifyContent="flex-end" className={classes.noWPActionGrid}>
                <Grid>
                  <Button
                    onClick={() => history.push('/logout')}
                    variant="contained"
                    color="primary"
                    className="noWPLogoutButton"
                    noMinHeight
                  >
                    Logout
                  </Button>
                </Grid>
              </Grid>
            ) : !LOADING ? (
              <Grid
                container
                justifyContent="flex-end"
                className={classes.siteDisciplineButtonGrid}
              >
                <Grid>
                  <Button
                    onClick={onConfirmSiteDiscipline}
                    variant="contained"
                    color="primary"
                    noMinHeight
                    isLoading={onConfirmSiteDisciplineClicked}
                    disabled={
                      !selectedSite || !selectedDiscipline || onConfirmSiteDisciplineClicked
                    }
                    className={
                      !selectedSite || !selectedDiscipline
                        ? 'confirmButtonDisabled'
                        : 'confirmButton'
                    }
                  >
                    <Typography className={classes.popUpConfirmLabel}>Confirm</Typography>
                  </Button>
                </Grid>
              </Grid>
            ) : (
              ''
            )}
          </>
        }
        maxWidth="xs"
        noMinHeight
      >
        {NO_WP_ASSIGNED ? (
          <Grid className={classes.noAssignedWPMessagGrid}>
            <Grid>
              <Grid container xs={12}>
                <Grid item xs={1}>
                  <Icons.InfoFeather color="error" />
                </Grid>
                <Grid item xs={11}>
                  <Typography className={classes.text}>
                    You don't have any assigned workpackages to perform inspection, please contact
                    site coordinator for further details
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        ) : (
          <Grid className={classes.siteSlectionGrid}>
            {LOADING ? (
              <Grid className={classes.circularProgressGrid}>
                <img src={`${IMAGE_PATH}/nutrien_dark_noline.svg`} width="100%" />
                <CircularProgress color="primary" />
              </Grid>
            ) : (
              <>
                <Grid item xs={12}>
                  <Typography className={`${classes.text} ${classes.sdSubHeading}`}>
                    Please select the site and discipline for which you want to perform inspection.
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="h6" className={classes.label}>
                    Select Site:
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Select
                    list={siteDisciplineObject ? Object.keys(siteDisciplineObject) : []}
                    required
                    onChange={(event, value) => {
                      handleChange(value, 'site');
                    }}
                    value={selectedSite || ''}
                    data-cy="PrimaryContextRecord-Autocomplete"
                    className={classes.firstAutoComplete}
                    noMinHeight
                    disabled={onConfirmSiteDisciplineClicked}
                    inputProps={{
                      'data-testid': 'sitedropdown',
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="h6" className={classes.label}>
                    Select Discipline:
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Select
                    list={isNull(siteDisciplineObject, selectedSite, [])}
                    required
                    onChange={(event, value) => {
                      handleChange(value, 'discipline');
                    }}
                    value={selectedDiscipline || 'null'}
                    data-cy="PrimaryContextRecord-Autocomplete"
                    className={classes.firstAutoComplete}
                    disabled={!selectedSite || onConfirmSiteDisciplineClicked}
                    noMinHeight
                    data-testid="disciplineDropdown"
                  />
                </Grid>
              </>
            )}
          </Grid>
        )}
      </Dialog>
    </>
  );
};
export default observer(SiteDisciplinePopUp);
